// Lag-permutation selector (1-day execution lag toggle).
//
// The market-health payload now carries BOTH execution-lag permutations for
// every strategy surface: `strategy[mode]`, each `history[i].strategy<Mode>`
// entry, and `current.signals[mode]` are shaped `{ lagOn: {...}, lagOff: {...} }`
// where each sub-object has exactly the fields the OLD flat object carried.
// The "1-day execution lag" toggle in MarketHealth.js picks which permutation
// every display reads, live, with no refetch (both arrive in the payload).
//
// `selectLag` is deliberately tolerant of the OLD FLAT shape too: if the value
// has neither a `lagOn` nor a `lagOff` key it's assumed to already be the flat
// object and returned as-is. This keeps pre-migration payloads (and any test
// fixture still using the flat shape) rendering correctly while the backend
// rolls out the nested shape in parallel.

/**
 * Given a lag-permuted object (`{ lagOn, lagOff }`) and the toggle state,
 * return the selected permutation. Falls back to the flat object when the
 * nested keys are absent, and to `null`/`undefined` passthrough when the
 * input itself is nullish.
 *
 * @param {object|null|undefined} permuted - `{ lagOn, lagOff }` or a flat object
 * @param {boolean} lag - true => 1-day lag ON (`lagOn`), false => OFF (`lagOff`)
 * @returns the selected permutation, or the flat object, or the nullish input
 */
export const selectLag = (permuted, lag) => {
  if (permuted == null) return permuted;
  const hasNested = Object.prototype.hasOwnProperty.call(permuted, 'lagOn')
    || Object.prototype.hasOwnProperty.call(permuted, 'lagOff');
  if (!hasNested) return permuted; // OLD flat shape — pass through unchanged.
  return lag ? permuted.lagOn : permuted.lagOff;
};

export default selectLag;
